import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  Checkbox, Input, Spin, Tag,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import debounce from "lodash/debounce";
import Scrollbar from "react-scrollbars-custom";

import { fetchTestCategory } from "../../../actions/chemistry";
import "./CategorizedTestSelector.css";

const DEFAULT_HEIGHT = 400;
const SEARCH_DEBOUNCE_MS = 400;
const CATEGORY_MATCH_SCOPE = "test_category";

function normalizeSearchQuery(query) {
  return query.trim().toLowerCase();
}

function testMatchesSearch(test, normalizedSearch) {
  return [test.internal_name, test.display_name].some(
    (name) => (name ?? "").toLowerCase().includes(normalizedSearch),
  );
}

function formatMatchingTestsLabel(count) {
  const noun = count === 1 ? "test" : "tests";
  return ` : ${count} matching ${noun}`;
}

function buildSelectedTest(test, matchScope) {
  return {
    match_scope: matchScope,
    test_category: test.categoryName,
    internal_name: test.internal_name ?? "",
    display_name: test.display_name ?? "",
    id: test.id,
  };
}

function buildSelectedCategory(category) {
  return {
    match_scope: CATEGORY_MATCH_SCOPE,
    test_category: category.test_category_name,
    internal_name: "",
    display_name: "",
  };
}

function isSameTest(selected, test) {
  if (selected?.id != null && test?.id != null) {
    return selected.id === test.id;
  }
  return selected?.display_name === test?.display_name
    && selected?.internal_name === test?.internal_name
    && selected?.test_category === test?.categoryName;
}

function getSelectedTestKey(selected, index, matchScope) {
  if (selected?.id != null) {
    return `selected-${selected.id}`;
  }
  const label = selected?.[matchScope] ?? selected?.internal_name ?? "";
  return `selected-${selected?.test_category}-${label}-${index}`;
}

function getRegularTestName(spec, matchScope) {
  if (!spec || typeof spec !== "object") return null;
  if (matchScope === CATEGORY_MATCH_SCOPE) {
    return spec.test_category ?? null;
  }
  return spec.internal_name ?? spec.display_name ?? null;
}

export default function CategorizedTestSelector({
  value = [],
  onChange,
  matchScope = "display_name",
  className = "",
  height = DEFAULT_HEIGHT,
  disabled = false,
}) {
  const isMounted = useRef(true);
  const previousMatchScopeRef = useRef(matchScope);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");

  const setDebouncedSearch = useMemo(
    () => debounce((q) => setDebouncedSearchInput(q), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => {
    setDebouncedSearch.cancel();
  }, [setDebouncedSearch]);

  useEffect(() => {
    if (previousMatchScopeRef.current === matchScope) return;

    const hasSelectionsForCurrentScope = value.length > 0
      && value.every((selected) => selected?.match_scope === matchScope);

    previousMatchScopeRef.current = matchScope;
    setDebouncedSearch.cancel();
    setSearchInput("");
    setDebouncedSearchInput("");

    if (!hasSelectionsForCurrentScope) {
      setSelectedCategoryId(categories[0]?.id ?? null);
    }

    if (!disabled && typeof onChange === "function" && value.length > 0 && !hasSelectionsForCurrentScope) {
      onChange([]);
    }
  }, [categories, disabled, matchScope, onChange, setDebouncedSearch, value]);

  useEffect(() => {
    const loadCategories = async () => {
      if (!isMounted.current) return;
      setLoading(true);
      const { success, results, message } = await fetchTestCategory();
      if (success) {
        const list = Array.isArray(results) ? results : [];
        setCategories(list);
      } else {
        toast.error(message);
        setCategories([]);
      }
      setLoading(false);
    };

    loadCategories();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryId(null);
      return;
    }

    const categoryExists = categories.some((cat) => cat.id === selectedCategoryId);
    if (selectedCategoryId === null || !categoryExists) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const normalizedSearch = normalizeSearchQuery(debouncedSearchInput);
  const isSearching = normalizedSearch.length > 0;
  const isCategoryMatchScope = matchScope === CATEGORY_MATCH_SCOPE;
  const selectedCategoryName = useMemo(
    () => value.find((selected) => selected?.test_category)?.test_category ?? null,
    [value],
  );
  const isTestSelected = useCallback(
    (test) => !isCategoryMatchScope && value.some((selected) => isSameTest(selected, test)),
    [isCategoryMatchScope, value],
  );

  const categorySelectedCounts = useMemo(() => {
    const counts = new Map();
    categories.forEach((category) => {
      const selectedCount = isCategoryMatchScope
        ? Number(selectedCategoryName === category.test_category_name)
        : value.filter((selected) => selected.test_category === category.test_category_name).length;
      counts.set(category.id, selectedCount);
    });
    return counts;
  }, [categories, isCategoryMatchScope, selectedCategoryName, value]);

  const categoryMatchCounts = useMemo(() => {
    if (!isSearching) return new Map();

    const counts = new Map();
    categories.forEach((category) => {
      const matchCount = (category.tests || []).filter(
        (test) => testMatchesSearch(test, normalizedSearch),
      ).length;
      counts.set(category.id, matchCount);
    });
    return counts;
  }, [categories, isSearching, normalizedSearch]);

  const selectedCategory = useMemo(
    () => categories.find((cat) => cat.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId],
  );

  const selectedCategoryTests = useMemo(() => {
    if (!selectedCategory) return [];

    return (selectedCategory.tests || []).map((test) => ({
      ...test,
      categoryId: selectedCategory.id,
      categoryName: selectedCategory.test_category_name,
    }));
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedCategoryName || !categories.length) return;

    const categoryWithSelection = categories.find(
      (category) => category.test_category_name === selectedCategoryName,
    );
    if (categoryWithSelection && categoryWithSelection.id !== selectedCategoryId) {
      setSelectedCategoryId(categoryWithSelection.id);
    }
  }, [categories, selectedCategoryId, selectedCategoryName]);

  const displayedTests = useMemo(() => {
    if (!selectedCategory) return [];

    if (isSearching) {
      return selectedCategoryTests.filter((test) => testMatchesSearch(test, normalizedSearch));
    }

    return selectedCategoryTests;
  }, [isSearching, normalizedSearch, selectedCategory, selectedCategoryTests]);

  const displayedSelectedCount = displayedTests.filter((test) => isTestSelected(test)).length;
  const isSelectAllChecked = displayedTests.length > 0
    && displayedSelectedCount === displayedTests.length;
  const isSelectAllIndeterminate = displayedSelectedCount > 0 && !isSelectAllChecked;

  const handleSearchChange = useCallback((e) => {
    const next = e.target.value;
    setSearchInput(next);
    if (next.trim() === "") {
      setDebouncedSearch.cancel();
      setDebouncedSearchInput("");
    } else {
      setDebouncedSearch(next);
    }
  }, [setDebouncedSearch]);

  const handleCategoryClick = useCallback((category) => {
    const isLockedByOtherCategory = !isCategoryMatchScope && selectedCategoryName
      && selectedCategoryName !== category.test_category_name;
    if (isLockedByOtherCategory) return;

    if (!isSearching) {
      setDebouncedSearch.cancel();
      setSearchInput("");
      setDebouncedSearchInput("");
    }
    setSelectedCategoryId(category.id);

    if (isCategoryMatchScope && !disabled && typeof onChange === "function") {
      onChange([buildSelectedCategory(category)]);
    }
  }, [disabled, isCategoryMatchScope, isSearching, onChange, selectedCategoryName, setDebouncedSearch]);

  const handleCategoryCheckboxChange = useCallback((category, checked) => {
    if (disabled || typeof onChange !== "function") return;

    setSelectedCategoryId(category.id);
    onChange(checked
      ? [buildSelectedCategory(category)]
      : value.filter((selected) => selected.test_category !== category.test_category_name));
  }, [disabled, onChange, value]);

  const handleTestToggle = useCallback((test, checked) => {
    if (disabled || isCategoryMatchScope) return;
    if (typeof onChange !== "function") return;
    if (checked && selectedCategoryName && selectedCategoryName !== test.categoryName) return;

    const prevTests = value.filter(
      (selected) => selected.test_category === test.categoryName && !isSameTest(selected, test),
    );

    const next = checked ? [...prevTests, buildSelectedTest(test, matchScope)] : prevTests;
    onChange(next);
  }, [disabled, isCategoryMatchScope, matchScope, onChange, selectedCategoryName, value]);

  const handleSelectAllToggle = useCallback((checked) => {
    if (disabled || isCategoryMatchScope) return;
    if (typeof onChange !== "function" || !selectedCategory) return;

    if (!checked) {
      onChange(value.filter((selected) => !displayedTests.some((test) => isSameTest(selected, test))));
      return;
    }

    const preserved = value.filter((selected) => (
      selected.test_category === selectedCategory.test_category_name
      && !displayedTests.some((test) => isSameTest(selected, test))
    ));
    onChange([...preserved, ...displayedTests.map((test) => buildSelectedTest(test, matchScope))]);
  }, [disabled, displayedTests, isCategoryMatchScope, matchScope, onChange, selectedCategory, value]);

  const handleRemoveSelected = useCallback((selectedToRemove) => {
    if (disabled || typeof onChange !== "function") return;
    if (isCategoryMatchScope) {
      onChange(value.filter((selected) => selected.test_category !== selectedToRemove.test_category));
      return;
    }
    onChange(value.filter((selected) => selected !== selectedToRemove));
  }, [disabled, isCategoryMatchScope, onChange, value]);

  const selectedItemsForDisplay = useMemo(() => {
    if (!isCategoryMatchScope) return value;

    const seenCategories = new Set();
    return value.filter((selected) => {
      if (!selected?.test_category || seenCategories.has(selected.test_category)) return false;
      seenCategories.add(selected.test_category);
      return true;
    });
  }, [isCategoryMatchScope, value]);

  const rootClassName = ["CategorizedTestSelector", className].filter(Boolean).join(" ");

  return (
    <Spin spinning={loading} className="CategorizedTestSelector__Spin">
      <div className="CategorizedTestSelector__Wrapper">
        <div className={rootClassName} style={{ height }}>
          <div className="CategorizedTestSelector__Sidebar">
            <Scrollbar
              className="CategorizedTestSelector__CategoryScrollbar"
              disableTrackYWidthCompensation
              disableTracksWidthCompensation
            >
              <ul className="CategorizedTestSelector__CategoryList">
                {categories.map((category) => {
                  const isActive = category.id === selectedCategoryId;
                  const isCategorySelected = selectedCategoryName === category.test_category_name;
                  const isLockedByOtherCategory = !isCategoryMatchScope
                    && selectedCategoryName
                    && !isCategorySelected;
                  const selectedCount = categorySelectedCounts.get(category.id) ?? 0;
                  const matchCount = categoryMatchCounts.get(category.id) ?? 0;
                  const showSelectedCount = selectedCount > 0 && !isCategoryMatchScope;
                  const showMatchCount = isSearching && matchCount > 0;

                  return (
                    <li key={category.id}>
                      <button
                        type="button"
                        disabled={!!isLockedByOtherCategory}
                        className={[
                          "CategorizedTestSelector__CategoryItem",
                          isActive ? "CategorizedTestSelector__CategoryItem--active" : "",
                          isLockedByOtherCategory ? "CategorizedTestSelector__CategoryItem--disabled" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {isCategoryMatchScope && (
                          <Checkbox
                            className="CategorizedTestSelector__CategoryCheckbox"
                            checked={isCategorySelected}
                            disabled={disabled || !!isLockedByOtherCategory}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => handleCategoryCheckboxChange(
                              category,
                              event.target.checked,
                            )}
                          />
                        )}
                        <span className="CategorizedTestSelector__CategoryName">
                          {category.test_category_name}
                        </span>
                        {showSelectedCount && (
                        <span className="CategorizedTestSelector__CategorySelectedCount">
                          {` (${selectedCount})`}
                        </span>
                        )}
                        {showMatchCount && (
                        <span className="CategorizedTestSelector__CategoryMatchCount">
                          {formatMatchingTestsLabel(matchCount)}
                        </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Scrollbar>
          </div>

          <div className="CategorizedTestSelector__Content">
            <Input
              allowClear
              className="CategorizedTestSelector__Search"
              placeholder="Start typing to search..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={handleSearchChange}
            />
            <div className="CategorizedTestSelector__Toolbar">
              <div className="CategorizedTestSelector__NameLegend">
                <span className="CategorizedTestSelector__TestInternalName">Internal name</span>
                <span className="CategorizedTestSelector__TestDisplayName">Display name</span>
              </div>
              <Checkbox
                className="CategorizedTestSelector__SelectAll"
                checked={isSelectAllChecked}
                indeterminate={isSelectAllIndeterminate}
                disabled={disabled || isCategoryMatchScope || displayedTests.length === 0}
                onChange={(event) => handleSelectAllToggle(event.target.checked)}
              >
                Select All
              </Checkbox>
            </div>

            <div className="CategorizedTestSelector__TestPanel">
              <Scrollbar
                className="CategorizedTestSelector__TestScrollbar"
                disableTrackYWidthCompensation
                disableTracksWidthCompensation
              >
                {displayedTests.length === 0 ? (
                  <div className="CategorizedTestSelector__Empty">
                    {isSearching ? "No items found" : "No tests in this category."}
                  </div>
                ) : (
                  <div className="CategorizedTestSelector__TestGrid">
                    {displayedTests.map((test) => (
                      <Checkbox
                        key={test.id}
                        className="CategorizedTestSelector__TestCheckbox"
                        disabled={disabled || isCategoryMatchScope
                          || (selectedCategoryName && selectedCategoryName !== test.categoryName)}
                        checked={isTestSelected(test)}
                        onChange={(e) => handleTestToggle(test, e.target.checked)}
                      >
                        <span
                          className="CategorizedTestSelector__TestLabel"
                          title={[test.internal_name, test.display_name].filter(Boolean).join(" — ")}
                        >
                          <span className="CategorizedTestSelector__TestInternalName">
                            {test.internal_name}
                          </span>
                          {test.display_name && (
                            <span className="CategorizedTestSelector__TestDisplayName">
                              {test.display_name}
                            </span>
                          )}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                )}
              </Scrollbar>
            </div>
          </div>
        </div>
        {selectedItemsForDisplay.length > 0 && (
          <div className="CategorizedTestSelector__SelectedTests">
            <div className="CategorizedTestSelector__SelectedTestsLabel">
              {matchScope === CATEGORY_MATCH_SCOPE ? "Selected Test Category:" : "Selected Tests:"}
            </div>
            <div className="CategorizedTestSelector__SelectedTestsChips">
              {selectedItemsForDisplay.map((selected, index) => {
                const label = getRegularTestName(selected, matchScope);
                if (!label) return null;

                return (
                  <Tag
                    key={getSelectedTestKey(selected, index, matchScope)}
                    className="CategorizedTestSelector__SelectedTag"
                    closable={!disabled}
                    onClose={(e) => {
                      e.preventDefault();
                      handleRemoveSelected(selected);
                    }}
                  >
                    <span
                      className="CategorizedTestSelector__SelectedTagLabel"
                      title={[label, selected.display_name].filter(Boolean).join(" — ")}
                    >
                      <span className="CategorizedTestSelector__TestInternalName">
                        {label}
                      </span>
                      {!isCategoryMatchScope && selected.display_name && (
                        <span className="CategorizedTestSelector__TestDisplayName">
                          {selected.display_name}
                        </span>
                      )}
                    </span>
                  </Tag>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
}
